import { getCurrencySymbol, isPlatformBrowser } from '@angular/common';
import {
	Component,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	inject,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ProfileComponent } from '@components/backoffice/account/profile/profile.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/auth/current-user.service';
import { BrandingInfo, BrandingService } from '@services/branding/branding.service';
import { ViewportService } from '@services/geo/viewport.service';
import { LOW_PROVIDER_BALANCE, ProviderBalanceService } from '@services/provider/provider-balance.service';
import { MenuService, PageAction, PageTabs } from '@services/shared/menu.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { PeriodPickerComponent } from '@shared/components/period-picker/period-picker.component';
import { TabsComponent } from '@shared/components/tabs/tabs.component';
import { SharedModule } from '@shared/modules/shared.module';
import { BRANDING } from 'app/core/constants/branding.constants';
import { LucideAngularModule } from 'lucide-angular';
import { filter, Subscription } from 'rxjs';

@Component({
	selector: 'app-profile-icon-bar',
	imports: [
		SharedModule,
		ButtonComponent,
		TabsComponent,
		PeriodPickerComponent,
		ProfileComponent,
		TranslocoModule,
		LucideAngularModule,
	],
	templateUrl: './profile-icon-bar.component.html',
	styleUrl: './profile-icon-bar.component.scss',
})
export class ProfileIconBarComponent implements OnInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);

	// Page state
	userName = '';
	/** Internal role name (admin, client...); the menu shows userRoleLabel */
	userRole = '';
	// Beltrashow's memoji for everyone until users can upload their own
	readonly userSticker = 'assets/layout/images/sticker-edu.png';
	pageTitle = '';
	isHomePage = false;
	isDashboard = false;
	showTitle = true;
	brandLogo: string = BRANDING.logo;
	readonly brandWordmark = BRANDING.topbarWordmark;
	readonly brandName = BRANDING.name;

	// Show/hide controls
	@Input() hideProfileButton = false;
	@Input() showHamburger = false;
	@Input() showOnlyTitle = false;
	@Input() isDetailPage = false;
	@Input() avatarOnly = false; // Only the avatar menu, floating (pages without topbar)
	@Input() hideTitle = false; // Only the brand (client mobile topbar)
	@Output() hamburgerClick = new EventEmitter<void>();
	@Output() backClick = new EventEmitter<void>();

	// Modal visibility states
	profileVisible = false;
	showProfileMenu = false;

	@ViewChild('profileMenu') profileMenu?: ElementRef;
	@ViewChild('avatarButton') avatarButton?: ElementRef;

	private routerSubscription = new Subscription();
	private brandingSubscription = new Subscription();
	private pageActionSubscription = new Subscription();
	pageAction: PageAction | null = null;
	private pageTabsSubscription = new Subscription();
	private titleSubscription: Subscription | null = null;
	pageTabs: PageTabs | null = null;

	private readonly pageTitles: { [key: string]: string } = {
		dashboard: 'pages.dashboard',
		boost: 'pages.boost',
		subscriptions: 'pages.subscriptions',
		'my-subscriptions': 'pages.mySubscriptions',
		orders: 'pages.orders',
		requests: 'pages.requests',
		users: 'pages.users',
		roles: 'pages.roles',
		permissions: 'pages.permissions',
		account: 'pages.account',
		profile: 'pages.profile',
		notifications: 'pages.notifications',
		configuration: 'pages.configuration',
	};

	private readonly resourceNames: { [key: string]: string } = {
		products: 'Producto',
		platforms: 'Plataforma',
		users: 'Usuario',
		orders: 'Pedido',
		requests: 'Solicitud',
		roles: 'Rol',
		permissions: 'Permiso',
		account: 'Cuenta',
	};

	private platformId = inject(PLATFORM_ID);
	private transloco = inject(TranslocoService);
	private brandingService = inject(BrandingService);
	private menuService = inject(MenuService);
	readonly viewport = inject(ViewportService);
	providerBalance = inject(ProviderBalanceService);
	readonly lowBalance = LOW_PROVIDER_BALANCE;

	constructor(
		public currentUserService: CurrentUserService,
		private authService: AuthService,
		private router: Router,
	) {}

	ngOnInit(): void {
		this.loadUserInfo();
		// Saving the own profile updates the stored user: show the new name and email right away
		this.currentUserService.currentUser$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.loadUserInfo());
		this.updatePageInfo(this.router.url);
		this.setupRouterSubscription();
		this.setupBrandingSubscription();
		// Deferred: the table publishes it during change detection of the page
		this.pageActionSubscription = this.menuService.pageAction$.subscribe((action) => {
			Promise.resolve().then(() => {
				this.pageAction = action;
			});
		});
		this.pageTabsSubscription = this.menuService.pageTabs$.subscribe((tabs) => {
			Promise.resolve().then(() => {
				this.pageTabs = tabs;
			});
		});

		if (isPlatformBrowser(this.platformId) && this.providerBalance.canView) this.providerBalance.start();
	}

	private setupBrandingSubscription(): void {
		this.brandingSubscription = this.brandingService.branding$.subscribe((branding: BrandingInfo | null) => {
			if (branding?.logo) {
				this.brandLogo = branding.logo;
			}
		});
	}

	ngOnDestroy(): void {
		this.routerSubscription.unsubscribe();
		this.pageActionSubscription.unsubscribe();
		this.pageTabsSubscription.unsubscribe();
		this.brandingSubscription?.unsubscribe();
		this.titleSubscription?.unsubscribe();
		// The balance poll is not stopped: this bar is recreated when the layout changes (e.g. rotating
		// the phone) and restarting it would wipe and reload the balance every time
	}

	/** One live title translation at a time (it used to pile up one subscription per navigation) */
	private translateTitle(key: string, apply: (text: string) => void): void {
		this.titleSubscription?.unsubscribe();
		this.titleSubscription = this.transloco.selectTranslate(key).subscribe(apply);
	}

	goToProfile(): void {
		this.profileVisible = true;
		this.showProfileMenu = false;
	}

	onProfileVisibleChange(visible: boolean): void {
		this.profileVisible = visible;
		if (!visible) {
			this.loadUserInfo();
		}
	}

	// Profile menu handlers
	toggleProfileMenu(event: Event): void {
		event.stopPropagation();
		this.showProfileMenu = !this.showProfileMenu;
	}

	closeProfileMenu(): void {
		this.showProfileMenu = false;
	}

	/** Escape closes the profile menu and returns the focus to the avatar button. */
	@HostListener('document:keydown.escape')
	onEscape(): void {
		if (!this.showProfileMenu) return;
		this.closeProfileMenu();
		this.avatarButton?.nativeElement.focus();
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: Event): void {
		if (this.showProfileMenu) {
			const target = event.target as HTMLElement;
			const clickedInside =
				this.profileMenu?.nativeElement.contains(target) || this.avatarButton?.nativeElement.contains(target);
			if (!clickedInside) {
				this.closeProfileMenu();
			}
		}
	}

	logout(): void {
		this.closeProfileMenu();
		// Never errors: the session is cleared locally and /login opens even if the backend call fails
		this.authService.logout().subscribe();
	}

	currencySymbol(currency: string | null): string {
		return getCurrencySymbol(currency ?? 'USD', 'narrow');
	}

	onPageTabChange(index: number): void {
		if (!this.pageTabs) return;
		this.pageTabs.onChange(index);
	}

	onHamburgerClick(): void {
		this.hamburgerClick.emit();
	}

	/** Translated role (users.roles.*, same labels as the users list); unknown roles keep their name */
	get userRoleLabel(): string {
		if (!this.userRole) return this.transloco.translate('pages.profile');
		const key = `users.roles.${this.userRole}`;
		const label = this.transloco.translate(key);
		return label === key ? this.userRole : label;
	}

	// Private helpers
	private loadUserInfo(): void {
		const currentUser = this.currentUserService.getCurrentUser();
		if (currentUser) {
			this.userName = currentUser.displayName || '';
			this.userRole = currentUser.role?.name || '';
		}
	}

	private setupRouterSubscription(): void {
		this.routerSubscription = this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event: any) => {
				this.updatePageInfo(event.url);
			});
	}

	private updatePageInfo(url: string): void {
		const cleanUrl = url.split('?')[0].split('#')[0];
		const segments = cleanUrl.split('/').filter((s) => s);

		this.showTitle = !segments.includes('admin-panel');
		this.isHomePage = segments[segments.length - 1] === 'home';
		this.isDashboard = segments[segments.length - 1] === 'dashboard';

		if (this.isHomePage) {
			const currentUser = this.currentUserService.getCurrentUser();
			if (currentUser?.displayName) {
				this.userName = currentUser.displayName;
			} else {
				this.translateTitle('menu.profile.defaultTitle', (translation) => {
					this.userName = translation;
				});
			}
		} else {
			const lastSegment = segments[segments.length - 1] || '';
			const titleKey = this.pageTitles[lastSegment];

			if (titleKey) {
				this.translateTitle(titleKey, (translation) => {
					this.pageTitle = translation;
				});
			} else {
				// Check if the last segment is a number (ID) - if so, show the "<resource> detail" title
				const isNumeric = /^\d+$/.test(lastSegment);
				if (isNumeric && segments.length > 1) {
					const resourceSegment = segments[segments.length - 2];
					const resourceName =
						this.resourceNames[resourceSegment] ||
						resourceSegment
							.split('-')
							.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
							.join(' ');
					this.translateTitle('pages.detail', (detailLabel) => {
						this.pageTitle = `${detailLabel} - ${resourceName}`;
					});
				} else {
					this.pageTitle = lastSegment
						.split('-')
						.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
						.join(' ');
				}
			}
		}
	}
}
